import { Bar, BarChart, Brush, CartesianGrid, Cell, Label, Legend, Line, LineChart, Pie, PieChart, Rectangle, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AddUser, CameraBlue, CutBox, Peoples } from '../../ui/assets/svgIcons/icon'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getRadiologistDash from '@custom/rtk-apis/analytics/getRadiologistDash';
import { Loader } from '@ohif/ui';

const RadiologistGraph = ({selectedYear}) => {
  const dispatch = useDispatch<AppDispatch>();
  const COLORS = ['#0f8491', '#0ea7a2', '#caffff', '#f2ffff', '#ffffff'];
  const radiologistDashResponse = useSelector((state: RootState) => state.getRadiologistDashResponse);
  const [isloading, setIsloading] = useState(false);
  const [graphType, setGraphType] = useState({
    numCases: { type: 'line' },
    modalityCases: { type: 'line' },
    TATmodality: { type: 'line' },
    TATpriority: { type: 'line' },
    numReports: { type: 'pie' },
    distributionReports: { type: 'pie' }
  })

  const [graphData, setGraphData] = useState({
    numCases: [],
    modalityCases: [],
    TATmodality: [],
    TATpriority: [],
    numReports: [],
    distributionReports: []
  });

  const handleChangeGraphType = (type, graphType) => {
    setGraphType(prevState => ({
      ...prevState,
      [type]: { type: graphType },
    }));
  };

  const fetchGraphData = async () => {
    setIsloading(true);
    const query = `?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`
    const radiologistDashResponse = await dispatch(getRadiologistDash(query)).unwrap();

    setGraphData((prev) => ({
      ...prev,
      numCases: radiologistDashResponse?.result?.monthlyReports || [],
      modalityCases: radiologistDashResponse?.result?.modalityCounts || [],
      TATmodality: radiologistDashResponse?.result?.aggregatedAnalytics || [],
      TATpriority: radiologistDashResponse?.result?.detailedAnalytics || [],
      numReports: radiologistDashResponse?.result?.statusCounts || [],
      distributionReports: radiologistDashResponse?.result?.priorityCounts || []
    }))
    setIsloading(false);
  }

  useEffect(() => {
    fetchGraphData();
  }, [selectedYear])

  const TATPriorityData = graphData?.TATpriority?.map((item) => ({
    ReportName: item.reportName || '',
    PriorityTAT: item.priorityTurnaroundTime || 0,
  }))

  const generateColor = (index) => {
    // this function will generate the similar random color based on project theme
    const baseColor = COLORS[index % COLORS.length];
    const base = parseInt(baseColor.slice(1), 16);
    const r = Math.min(255, Math.max(0, (base >> 16) + Math.floor(Math.random() * 50) - 25));
    const g = Math.min(255, Math.max(0, ((base >> 8) & 0xff) + Math.floor(Math.random() * 50) - 25));
    const b = Math.min(255, Math.max(0, (base & 0xff) + Math.floor(Math.random() * 50) - 25));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };


  return (
    <div>
      {/* Executive Summary Row */}
      <div className='card-outer'>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-1' src={CameraBlue} alt='icon' />
            </div>
          </div>
          <div className='mt-2'>Total Patients</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>{radiologistDashResponse?.result?.totalReports || '--'}</div>
            <div className='green-label'>{'--%'}</div>
          </div>
        </div>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-2' src={Peoples} alt='icon' />
            </div>
          </div>
          <div className='mt-2'>Total Active Patient</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>{'--'}</div>
            <div className='green-label'>{'--%'}</div>
          </div>
        </div>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-3' src={AddUser} alt='icon' />
            </div>
          </div>
          <div className='mt-2'>New Patient</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>{'--'}</div>
            <div className='green-label'>{'--%'}</div>
          </div>
        </div>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-4' src={CutBox} alt='icon' />
            </div>
          </div>
          <div className='mt-2'>Patient Retention Rate</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>--</div>
            <div className='green-label'>--%</div>
          </div>
        </div>
      </div>
      {/* Case Volume */}
      <div className='analytics-card-container mt-4 mb-4'>
        <div>Case Volume</div>
        <div className='grid-2-container'>
          <div className="grid-item analytics-card-container">
            <h3>Number of cases</h3>
            <span className='flex gap-2.5 justify-center items-center'>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="line"
                  checked={graphType.numCases.type === 'line'}
                  onChange={() => handleChangeGraphType('numCases', 'line')}
                />
                <span className="ml-1">Line Graph</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="bar"
                  checked={graphType.numCases.type == 'bar'}
                  onChange={() => handleChangeGraphType('numCases', 'bar')}
                />
                <span className="ml-1">Bar Graph</span>
              </label>
            </span>
            <div>
              {graphData?.numCases?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (graphType.numCases.type === 'line' ?
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart data={graphData.numCases} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" padding={{ left: 30, right: 30 }}>
                      <Label value="Months" offset={-15} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label
                        value="Reports"
                        angle={-90}
                        offset={10}
                        position="insideLeft"
                      />
                    </YAxis>
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke={COLORS[0]}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                :
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={graphData.numCases}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month">
                      <Label value="Months" offset={-5} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label value="Reports" angle={-90} offset={10} position="insideLeft" />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="count" fill={COLORS[1]} activeBar={<Rectangle fill={COLORS[2]} stroke={COLORS[1]} />} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <h3>Cases By Modality</h3>
            <span className='flex gap-2.5 justify-center items-center'>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="bar"
                  checked={graphType.modalityCases.type === 'bar'}
                  onChange={() => handleChangeGraphType('modalityCases', 'bar')}
                />
                <span className="ml-1">Bar Graph</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="line"
                  checked={graphType.modalityCases.type == 'line'}
                  onChange={() => handleChangeGraphType('modalityCases', 'line')}
                />
                <span className="ml-1">Line Graph</span>
              </label>
            </span>
            <div>
              {graphData?.modalityCases?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (graphType.modalityCases.type === 'line' ?
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart data={graphData.modalityCases} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" padding={{ left: 30, right: 30 }}>
                      <Label value="Modality" offset={-15} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label
                        value="Reports"
                        angle={-90}
                        offset={10}
                        position="insideLeft"
                      />
                    </YAxis>
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke={COLORS[0]}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                :
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={graphData.modalityCases}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label value="Modality" offset={-5} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label value="Counts" angle={-90} offset={10} position="insideLeft" />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="count" fill={COLORS[1]} activeBar={<Rectangle fill={COLORS[2]} stroke={COLORS[1]} />} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Turn Around Time */}
      <div className='analytics-card-container mt-4 mb-4'>
        <div>Turn Around Time</div>
        <div className='grid-2-container'>
          <div className="grid-item analytics-card-container">
            <h3>Turnaround Time by Modality</h3>
            <span className='flex gap-2.5 justify-center items-center'>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="line"
                  checked={graphType.TATmodality.type === 'line'}
                  onChange={() => handleChangeGraphType('TATmodality', 'line')}
                />
                <span className="ml-1">Line Graph</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="bar"
                  checked={graphType.TATmodality.type == 'bar'}
                  onChange={() => handleChangeGraphType('TATmodality', 'bar')}
                />
                <span className="ml-1">Bar Graph</span>
              </label>
            </span>
            <div>
              {graphData?.TATmodality?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                <>
                  {graphType?.TATmodality?.type === 'line' ? (
                    <ResponsiveContainer width="100%" height={500}>
                      <LineChart data={graphData.TATmodality} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="modality" padding={{ left: 30, right: 30 }}>
                          <Label value="Modality" offset={-20} position="insideBottom" style={{ paddingTop: '10px' }} />
                        </XAxis>
                        <YAxis>
                          <Label value="Average TAT & Counts" angle={-90} offset={10} position="insideLeft" />
                        </YAxis>
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke={COLORS[0]} activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="averageTAT" stroke={COLORS[1]} activeDot={{ r: 8 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart
                        data={graphData.TATmodality}
                        margin={{
                          top: 20,
                          right: 20,
                          left: 20,
                          bottom: 30,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="modality">
                          <Label value="Modality" offset={-15} position="insideBottom" />
                        </XAxis>
                        <YAxis>
                          <Label value="Average TAT & Counts" angle={-90} offset={10} position="insideLeft" />
                        </YAxis>
                        <Tooltip />
                        <Bar dataKey="count" fill={COLORS[0]} />
                        <Bar dataKey="averageTAT" fill={COLORS[1]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </>
              )}
            </div>

          </div>
          <div className="grid-item analytics-card-container">
            <h3>Turnaround time by Priority </h3>
            <span className='flex gap-2.5 justify-center items-center'>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="bar"
                  checked={graphType.TATpriority.type === 'bar'}
                  onChange={() => handleChangeGraphType('TATpriority', 'bar')}
                />
                <span className="ml-1">Bar Graph</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="line"
                  checked={graphType.TATpriority.type == 'line'}
                  onChange={() => handleChangeGraphType('TATpriority', 'line')}
                />
                <span className="ml-1">Line Graph</span>
              </label>
            </span>
            <div>
              {graphData?.TATpriority?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart.. </p>
                </div>
              ) : (graphType.TATpriority.type === 'bar' ?
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={TATPriorityData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ReportName" interval="preserveStartEnd" textAnchor="end">
                      <Label value="Reports" offset={10} position="insideBottom" />
                    </XAxis>
                    <YAxis
                      tick={false}
                      label={{
                        value: 'Time (seconds)',
                        angle: -90,
                        position: 'center',
                      }}
                    />
                    <Tooltip />
                    {/* <Legend verticalAlign="top" wrapperStyle={{ lineHeight: '40px' }} /> */}
                    <ReferenceLine y={0} stroke="#000" />
                    <Brush
                      dataKey="reportName"
                      height={40}
                      stroke={COLORS[0]}
                      startIndex={0}
                      endIndex={Math.min(29, TATPriorityData?.length - 1)}
                      travellerWidth={10}
                    />
                    <Bar dataKey="PriorityTAT" fill={COLORS[0]} activeBar={<Rectangle fill={COLORS[1]} stroke={COLORS[4]} />} />
                  </BarChart>
                </ResponsiveContainer>
                :
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart data={TATPriorityData} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ReportName" padding={{ left: 30, right: 30 }}>
                      <Label value="Reports" offset={-15} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label
                        value="Time (seconds)"
                        angle={-90}
                        offset={10}
                        position="insideLeft"
                      />
                    </YAxis>
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="PriorityTAT"
                      stroke={COLORS[0]}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Report Status */}
      <div className='analytics-card-container mt-4 mb-4'>
        <div>Report Status</div>
        <div className='grid-2-container'>
          <div className="grid-item analytics-card-container">
            <h3>Report Completion Metrics</h3>
            <span className='flex gap-2.5 justify-center items-center'>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="pie"
                  checked={graphType.numReports.type === 'pie'}
                  onChange={() => handleChangeGraphType('numReports', 'pie')}
                />
                <span className="ml-1">Pie Graph</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="bar"
                  checked={graphType.numReports.type == 'bar'}
                  onChange={() => handleChangeGraphType('numReports', 'bar')}
                />
                <span className="ml-1">Bar Graph</span>
              </label>
            </span>
            <div>
              {graphData?.numReports?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (graphType.numReports.type === 'pie' ?
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      dataKey="count"
                      isAnimationActive={false}
                      data={graphData.numReports}
                      cx="50%"
                      cy="50%"
                      outerRadius={130}
                      label={({ name, count }) => `${name}: ${count}`}
                    >
                      {graphData.numReports.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || generateColor(index)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                :
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={graphData.numReports}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label value="Status" offset={-5} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label value="Counts" angle={-90} offset={10} position="insideLeft" />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="count" fill={COLORS[0]} activeBar={<Rectangle fill={COLORS[1]} stroke={COLORS[2]} />} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <h3>Report Status Distribution Priority</h3>
            <span className='flex gap-2.5 justify-center items-center'>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="bar"
                  checked={graphType.distributionReports.type === 'bar'}
                  onChange={() => handleChangeGraphType('distributionReports', 'bar')}
                />
                <span className="ml-1">Bar Graph</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="pie"
                  checked={graphType.distributionReports.type == 'pie'}
                  onChange={() => handleChangeGraphType('distributionReports', 'pie')}
                />
                <span className="ml-1">Pie Graph</span>
              </label>
            </span>
            <div>
              {graphData?.distributionReports?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (graphType.distributionReports.type === 'bar' ?
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={graphData.distributionReports}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label value="Priority" offset={-5} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label value="Counts" angle={-90} offset={10} position="insideLeft" />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="count" fill={COLORS[0]} activeBar={<Rectangle fill={COLORS[1]} stroke={COLORS[1]} />} />
                  </BarChart>
                </ResponsiveContainer>
                :
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      dataKey="count"
                      isAnimationActive={false}
                      data={graphData.distributionReports}
                      cx="50%"
                      cy="50%"
                      outerRadius={130}
                      label={({ name, count }) => `${name}: ${count}`}
                    >
                      {graphData.distributionReports.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || generateColor(index)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={isloading} />
    </div>
  )
}

export default RadiologistGraph
